import axios from "./_axios";
import { API } from "./apiEndPoints";
import configService from "../services/config";

/**
 * Unescape special characters in text extracted from Python dict format
 * @param {string} text - The text to unescape
 * @returns {string} Unescaped text
 */
const unescapeText = (text) => {
  return text
    .replace(/\\n/g, "\n")
    .replace(/\\'/g, "'")
    .replace(/\\"/g, '"')
    .replace(/\\\\/g, "\\");
};

/**
 * Check if a string contains metadata markers that should be skipped
 * @param {string} text - The text to check
 * @returns {boolean} True if text contains metadata markers
 */
const containsMetadataMarkers = (text) => {
  return text.includes("'extras'") || text.includes("'signature'");
};

/**
 * Check if text looks like a Python dict that needs parsing
 * @param {string} text - The text to check
 * @returns {boolean} True if text looks like a Python dict
 */
const isPythonDictFormat = (text) => {
  return text.includes("'type':") || text.includes('"type":');
};

/**
 * Extract text value from a Python dict formatted string
 * @param {string} dictStr - The dict string to parse
 * @returns {string|null} Extracted text or null if not found
 */
const extractTextFromDict = (dictStr) => {
  const textMatch = dictStr.match(/'text'\s*:\s*'((?:[^'\\]|\\.)*)'/);
  if (textMatch && textMatch[1] !== undefined) {
    const text = textMatch[1];
    if (text && text.trim() !== "") {
      return unescapeText(text);
    }
  }
  return null;
};

/**
 * Parse concatenated Python-style dict strings and extract text content
 * Handles format like: {'type': 'text', 'text': 'content', 'index': 0}{'type': 'text', 'text': 'more', 'index': 0}
 * @param {string} rawAnswer - The raw answer string with concatenated dicts
 * @returns {string} Extracted text content
 */
const parseAnswerText = (rawAnswer) => {
  if (!rawAnswer || typeof rawAnswer !== "string") return rawAnswer;

  if (!isPythonDictFormat(rawAnswer)) {
    return rawAnswer;
  }

  try {
    const extractedTexts = [];
    const parts = rawAnswer.split(/\}\s*\{/);

    for (let i = 0; i < parts.length; i++) {
      let part = parts[i];
      if (i > 0) part = "{" + part;
      if (i < parts.length - 1) part = part + "}";

      if (containsMetadataMarkers(part)) {
        continue;
      }

      const text = extractTextFromDict(part);
      if (text) {
        extractedTexts.push(text);
      }
    }

    if (extractedTexts.length > 0) {
      return extractedTexts.join("");
    }
  } catch (e) {
    console.warn("Failed to parse answer text:", e);
  }

  return rawAnswer;
};

/**
 * Ask IQ API - Send a question to the IQ service
 * @param {Object} params - Request parameters
 * @param {string} params.question - The question to ask
 * @param {number} params.k - The k parameter (default: 5)
 * @param {AbortSignal} params.signal - Optional abort signal for cancellation
 * @param {string} params.customEndpoint - Optional custom endpoint URL (overrides default)
 * @param {Object} params.customPayload - Optional custom payload structure (overrides default)
 * @returns {Promise} API response
 */
export const askIQ = async (params) => {
  const { question, k = 5, signal, customEndpoint, customPayload } = params;

  const iqBaseUrl = configService.get("iqBaseUrl");

  const axiosInstance = axios(true, {
    baseURL: customEndpoint ? "" : iqBaseUrl,
  });

  const payload = customPayload || { question, k };

  const result = await axiosInstance({
    method: "post",
    url: customEndpoint || API.ASK_IQ,
    data: payload,
    signal,
  });

  return result;
};

/**
 * Parse streaming text content from Python dict format if needed
 * @param {string} textContent - The text content to parse
 * @returns {{text: string|null, skip: boolean}} Parsed text and skip flag
 */
const parseStreamingTextContent = (textContent) => {
  if (typeof textContent !== "string" || !isPythonDictFormat(textContent)) {
    return { text: textContent, skip: false };
  }

  if (containsMetadataMarkers(textContent)) {
    return { text: null, skip: true };
  }

  const extracted = extractTextFromDict(textContent);
  if (extracted === null) {
    return { text: null, skip: true };
  }

  return { text: extracted, skip: false };
};

/**
 * Process a text event from SSE stream
 * @param {Object} data - The parsed JSON data
 * @param {string} accumulatedThinking - Current accumulated thinking
 * @param {Function} onThinkingUpdate - Callback for updates
 * @returns {{accumulated: string, skip: boolean}} Updated state
 */
const processTextEvent = (data, accumulatedThinking, onThinkingUpdate) => {
  if (data.text === undefined) {
    return { accumulated: accumulatedThinking, skip: false };
  }

  const { text, skip } = parseStreamingTextContent(data.text);
  if (skip || text === null) {
    return { accumulated: accumulatedThinking, skip: true };
  }

  const newAccumulated = accumulatedThinking + text;
  if (onThinkingUpdate) {
    onThinkingUpdate(newAccumulated);
  }

  return { accumulated: newAccumulated, skip: false };
};

/**
 * Process a complete event from SSE stream (answer, suggestions, or video instructions)
 * @param {Object} data - The parsed JSON data
 * @param {Function} onComplete - Callback when complete
 * @returns {Object|null} The final response or null
 */
const processCompleteEvent = (data, onComplete) => {
  // Check for any of the expected complete response types:
  // - answer: regular Q&A response
  // - suggestions: checkpoint/responsibility suggestions
  // - instructions: video instructions response
  // - validation: video validation response (PASS or FAIL)
  const isCompleteResponse =
    data.answer !== undefined ||
    data.suggestions !== undefined ||
    data.instructions !== undefined ||
    data.validation !== undefined;

  if (!isCompleteResponse) {
    return null;
  }

  const parsedAnswer = data.answer ? parseAnswerText(data.answer) : null;
  const finalResponse = { ...data, answer: parsedAnswer };

  if (onComplete) {
    onComplete(finalResponse);
  }

  return finalResponse;
};

/**
 * Process a single SSE line
 * @param {string} line - The line to process
 * @param {Object} state - Current processing state
 * @param {Object} callbacks - Callbacks for events
 * @returns {Object} Updated state
 */
const processSSELine = (line, state, callbacks) => {
  const trimmedLine = line.trim();

  if (!trimmedLine || trimmedLine.startsWith("event:")) {
    return state;
  }

  if (!trimmedLine.startsWith("data:")) {
    return state;
  }

  const dataStr = trimmedLine.slice(5).trim();
  if (!dataStr) {
    return state;
  }

  try {
    const data = JSON.parse(dataStr);

    const textResult = processTextEvent(
      data,
      state.accumulatedThinking,
      callbacks.onThinkingUpdate
    );
    const newState = { ...state, accumulatedThinking: textResult.accumulated };

    const completeResult = processCompleteEvent(data, callbacks.onComplete);
    if (completeResult) {
      newState.finalResponse = completeResult;
    }

    return newState;
  } catch (parseError) {
    console.warn("Failed to parse SSE data:", dataStr, parseError);
    return state;
  }
};

/**
 * Read and process SSE stream
 * @param {ReadableStreamDefaultReader} reader - The stream reader
 * @param {Object} callbacks - Callbacks for events
 * @returns {Promise<Object>} The final response data
 */
const processSSEStream = async (reader, callbacks) => {
  const decoder = new TextDecoder();
  let state = {
    accumulatedThinking: "",
    finalResponse: null,
    buffer: "",
  };

  try {
    let done = false;
    while (!done) {
      const result = await reader.read();
      done = result.done;

      if (done) break;

      state.buffer += decoder.decode(result.value, { stream: true });
      const lines = state.buffer.split("\n");
      state.buffer = lines.pop() || "";

      for (const line of lines) {
        state = processSSELine(line, state, callbacks);
      }
    }
  } finally {
    reader.releaseLock();
  }

  return {
    finalResponse: state.finalResponse,
    accumulatedThinking: state.accumulatedThinking,
  };
};

/**
 * Build fetch request configuration
 * @param {string} url - The request URL
 * @param {Object} payload - The request payload
 * @param {AbortSignal} signal - Optional abort signal
 * @returns {Object} Fetch request configuration
 */
const buildFetchConfig = (url, payload, signal) => {
  const token = localStorage.getItem("token");
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: "Token " + token } : {}),
    },
    body: JSON.stringify(payload),
    signal,
  };
};

/**
 * Ask IQ API with SSE Streaming - Send a question and receive streaming response
 * @param {Object} params - Request parameters
 * @param {string} params.question - The question to ask
 * @param {number} params.k - The k parameter (default: 5)
 * @param {AbortSignal} params.signal - Optional abort signal for cancellation
 * @param {string} params.customEndpoint - Optional custom endpoint URL (overrides default)
 * @param {Object} params.customPayload - Optional custom payload structure (overrides default)
 * @param {Function} params.onThinkingUpdate - Callback for streaming thinking text updates
 * @param {Function} params.onComplete - Callback when streaming is complete with final response
 * @returns {Promise} Final API response
 */
export const askIQStream = async (params) => {
  const {
    question,
    k = 5,
    signal,
    customEndpoint,
    customPayload,
    onThinkingUpdate,
    onComplete,
  } = params;

  const iqBaseUrl = configService.get("iqBaseUrl");
  const url = customEndpoint || `${iqBaseUrl.replace(/\/$/, "")}/${API.ASK_IQ}`;
  const payload = customPayload || { question, k };

  const response = await fetch(url, buildFetchConfig(url, payload, signal));

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const reader = response.body.getReader();
  const { finalResponse, accumulatedThinking } = await processSSEStream(
    reader,
    { onThinkingUpdate, onComplete }
  );

  return {
    data: finalResponse || {
      answer: accumulatedThinking,
      reasoning: accumulatedThinking,
    },
  };
};
